import { useEffect, useMemo, useState } from "react";
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, arrayMove } from "@dnd-kit/sortable";
import { createPortal } from "react-dom";
import { PlusIcon } from "lucide-react";
import ColumnContainer from "./task-board-column";
import { DictType, Task } from "@pma/types";
import KanbanTaskCard from "./kanban-task-card";
import { useModal } from "@stores/modal-store";
import axios from "axios";
import { EDIT_TASK_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes";
import { useMutation } from "@tanstack/react-query";
import { parseCookies } from "nookies";
import { ACCESS_TOKEN, queryClient } from "@pma/utils/constants";
import { useNavigate, useSearchParams } from "react-router-dom";
import useTranslator from "@hooks/use-translator";
import { groupTaskByStatus } from "@pma/utils/utils";
import { useAuth } from "@pma/stores/auth-store";

interface Props {
  tasks: Task[];
}

function TasksKanbanBoard({ tasks: projectTasks }: Props) {
  const [columns, setColumns] = useState<any[]>([]);
  const [activeColumn, setActiveColumn] = useState<any | null>(null);
  const [activeTask, setActiveTask] = useState<any | null>(null);
  const [tasks, setTasks] = useState<any[]>([]);
  const [compute, setCompute] = useState(false);

  const modal = useModal();
  const kanbanKey = 'kanban-order'
  const navigate = useNavigate();
  const { translate } = useTranslator();

  const [searchParam] = useSearchParams();
  const newParams = new URLSearchParams(searchParam); 

  const accessToken = parseCookies()[ACCESS_TOKEN];
  const { authConfig } = useAuth();
  
    const userId = authConfig?.id;
  
  const columnOrder = localStorage.getItem(kanbanKey)
  useEffect(()=>{
    if(!columnOrder){
      localStorage.setItem(kanbanKey,JSON.stringify([]))
    }
    if(columnOrder){
      const userArr = JSON.parse(columnOrder)
      const found = userArr.find((val:any)=>val.user===userId)

      if(!found){
        localStorage.setItem(kanbanKey,JSON.stringify([...userArr,{user:userId,data:[]}]))
      }
    }
  },[])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
  );

  const findCol = (id:number,iId:number)=>{
    let temp = iId
    const arr = localStorage?.getItem(kanbanKey)
    if(arr){
      
      const userArr = JSON.parse(arr)

      if(userArr && userArr.length > 0){
        const found = userArr.find((val:any)=>val.user===userId )

        if(found){
          const userOrder = found.data
           const entry = userOrder.find((val:{id:number,order:number})=>id===val.id)
        if(entry){
          temp = entry.order
        }
        }
      }
    }
   return temp 
  }

  const statusColumns = useMemo(() => {
    const groupedTasks = projectTasks && groupTaskByStatus(projectTasks);
    
    const allTasks: Array<any> = [];
    let cols :any = []
    if(groupedTasks.length > 0){
       cols = groupedTasks?.map((group,index) => {
        allTasks.push(...group?.tasks);
        return {
          id: group?.statuses?.id,
          title: group?.statuses?.name,
          count: group?.tasks?.length,
          tasks: group?.tasks,
          color: group?.statuses?.color,
          order: findCol(group?.statuses?.id,index) 
        };
      });
     
      setTasks(allTasks)
    }
    return cols.sort(
      (a: DictType, b: DictType) => a.order - b.order);
  }, [projectTasks, compute]);

  const columnsId = useMemo(
    () => statusColumns?.map((col: DictType) => col.id),
    [statusColumns],
  );

  function generateId() {
    /* Generate a random number between 0 and 10000 */
    return Math.floor(Math.random() * 10001);
  }

  function createTask(columnId: any) {
    const newTask: any = {
      id: generateId(),
      columnId,
      title: `Task ${tasks.length + 1}`,
    };

    setTasks([...tasks, newTask]);
  }

  function deleteTask(id: any) {
    const newTasks = tasks.filter((task) => task.id !== id);
    setTasks(newTasks);
  }

  function updateTask(id: any, content: string) {
    const newTasks = tasks?.map((task) => {
      if (task?.id !== id) return task;
      return { ...task, content };
    });

    setTasks(newTasks);
  }

  // function createNewColumn() {
  //   const columnToAdd: any = {
  //     id: generateId(),
  //     title: `Column ${columns.length + 1}`,
  //   };

  //   setColumns([...columns, columnToAdd]);
  // }

  function deleteColumn(id: any) {
    const filteredColumns = columns.filter((col) => col.id !== id);
    setColumns(filteredColumns);

    const newTasks = tasks.filter((t) => t.columnId !== id);
    setTasks(newTasks);
  }

  function updateColumn(id: any, title: string) {
    const newColumns = columns?.map((col) => {
      if (col.id !== id) return col;
      return { ...col, title };
    });

    setColumns(newColumns);
  }

  const updateTaskStatus = async (task: DictType, status_id: number) => {
    const payload = {
      title: task.title,
      description: task.dec,
      status_id: status_id,
      task_id: task.id,
      inform_users: true,
    };
     await axios.post(
      SERVER_URL + EDIT_TASK_ENDPOINT,
      payload,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    ) .then((res) => {
      const {status} = res
      if(status===200){
        queryClient.invalidateQueries({ queryKey: ['get-tasks'] })
      }
      return res
    })
    .catch((error) => {
      const errorMessage = new Error(error.message)
      console.error(error.message);
      return errorMessage
    });;

    
  };

  const updateTaskStatusMutation = useMutation({
    mutationKey: ["update-kanban-task-status"],
    mutationFn: ({ task, status_id }: DictType) =>
      updateTaskStatus(task, status_id),
    onSuccess: (data) => {
      // TODO
      // newParams.set('revalidate', 'tasks');
      // navigate(`?${newParams.toString()}`);
    },
    onError: (error) => {
      //  TODO
    },
  });

  function onDragStart(event: DragStartEvent) {
    if (event.active.data.current?.type === "Column") {
      setActiveColumn(event.active.data.current.column);
      return;
    }

    if (event.active.data.current?.type === "Task") {
      setActiveTask(event.active.data.current.task);
      return;
    }
  }

  function onDragEnd(event: DragEndEvent) {
    setActiveColumn(null);
    setActiveTask(null);

    const { active, over } = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) return;

    const isActiveAColumn = active.data.current?.type === "Column";
    if (!isActiveAColumn) return;

    // Drag end
    setColumns((columns) => {
      const activeColumnIndex = columns?.findIndex(
        (col) => col.id === activeId,
      );

      const overColumnIndex = columns?.findIndex((col) => col.id === overId);

      const arr = arrayMove(columns, activeColumnIndex, overColumnIndex);
     const tArr = arr.map((val,index)=>{
        val.order = index
        return {id:val.id,order:val.order}
      })

      if(columnOrder){
        const userArr = JSON.parse(columnOrder)
        if(userArr && userArr.length > 0){
          const found = userArr.find((val:any)=>val.user===userId)
          
          if(found){
            const index = userArr.indexOf(found)
            userArr[index]= {user:userId,data:tArr}            
            localStorage.setItem(kanbanKey,JSON.stringify(userArr))
          }
        }else{
          localStorage.setItem(kanbanKey,JSON.stringify([{data:tArr,user:userId}]))
        }
        
      }
      return arr
    });
  }

  function onDragOver(event: DragOverEvent) {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;
    if (activeId === overId) return;

    const isActiveATask = active.data.current?.type === "Task";
    const isOverATask = over.data.current?.type === "Task";

    if (!isActiveATask) return;

    // Dropping a Task over another Task
    if (isActiveATask && isOverATask) {
     
        const activeIndex = tasks.findIndex((t) => t.id === activeId);
        const overIndex = tasks.findIndex((t) => t.id === overId);

        if (tasks[activeIndex].status_id != tasks[overIndex].status_id) {
          tasks[activeIndex].status_id = tasks[overIndex].status_id;

          // return arrayMove(tasks, activeIndex, overIndex - 1);
          const arr = [...tasks]
          const newArr =  arrayMove([...arr], activeIndex, overIndex - 1)
          setTasks([...newArr])
        }else{
          const arr = [...tasks]
          const newArr = arrayMove([...arr], activeIndex, overIndex);
          setTasks([...newArr])
        }
        
        if(over.data.current && over.data.current?.task){

          updateTaskStatusMutation.mutate({
            task: activeTask,
            status_id:over.data.current.task.status_id,
          });
        }
       
    }
    
    const isOverAColumn = over.data.current?.type === "Column";

    // dropping a Task over a column
    if (isActiveATask && isOverAColumn) {
      const activeIndex = tasks?.findIndex((t) => t?.id === activeId);
      const overIndex = tasks?.findIndex((t) => t?.id === overId);
      
      const activeTask = tasks[activeIndex];

      activeTask.status_id = overId;

      // setCompute(true);
      const arr = [...tasks]
      const newArr = arrayMove([...arr], activeIndex, activeIndex);
      setTasks([...newArr])

      updateTaskStatusMutation.mutate({
        task: activeTask,
        status_id: over?.id,
      });
    }
  }

  useEffect(() => {
    setColumns(statusColumns);
  }, [projectTasks]);

  if (projectTasks) {
    return (
      <div className="flex justify-start w-full p-1 items-center overflow-x-auto overflow-y-hidden py-2">
        <DndContext
          sensors={sensors}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragOver={onDragOver}
        >
          <div className="flex gap-4">
            <div className="flex  gap-4">
              <SortableContext items={columnsId}>
                {columns?.map((col) => (
                  <ColumnContainer
                    key={col.id}
                    column={col}
                    deleteColumn={deleteColumn}
                    updateColumn={updateColumn}
                    createTask={createTask}
                    deleteTask={deleteTask}
                    updateTask={updateTask}
                    tasks={[...tasks?.filter((task) => task?.status_id === col.id)]}
                  />
                ))}
              </SortableContext>
            </div>
            <button
              onClick={() => {
                modal.onOpen();
                modal.setContext("task");
              }}
              className="flex items-center h-[60px] w-[350px] min-w-[350px] cursor-pointer gap-2 rounded-lg border-1 border-primary bg-primary/20 p-4 text-primary ring-slate-300 hover:ring-2"
            >
              <PlusIcon />
              {translate("Add Task")}{" "}
            </button>
          </div>

          {createPortal(
            <DragOverlay>
              {activeColumn && (
                <ColumnContainer
                  column={activeColumn}
                  deleteColumn={deleteColumn}
                  updateColumn={updateColumn}
                  createTask={createTask}
                  deleteTask={deleteTask}
                  updateTask={updateTask}
                  tasks={[...tasks.filter(
                    (task) => task?.status_id === activeColumn.id,
                  )]}
                />
              )}
              {activeTask && (
                <KanbanTaskCard
                  task={activeTask}
                  deleteTask={deleteTask}
                  updateTask={updateTask}
                />
              )}
            </DragOverlay>,
            document.body,
          )}
        </DndContext>
      </div>
    );
  }
}

export default TasksKanbanBoard;
